/**
 * 🏠 Where are you standing — open ground, the lee of a wall, or indoors? (T132)
 *
 * This is **the one question nothing in the game could answer** until now, and
 * it is what **D64** / [ADR-0016](../../../Project/adr/0016-cold-is-charged-to-the-food-meter.md)
 * §3 spends: the shelter ladder is *open ground · lee · enclosed · a fire
 * indoors*, and each rung takes something away from the cold. **T131 charges the
 * result to the food bar. This file only answers where you are.**
 *
 * ## 🔴 Four rungs, never a number
 *
 * 🎯 **haugur, 2026-08-21**, asked directly because the exposure gauge already
 * drawn on the HUD is a sliding scale and D64 says four discrete states: **the
 * four rungs are the truth and the gauge follows them.**
 *
 * ⚠️ **D64 §6 bans a temperature field**, and the reason is the one that decides
 * it: *its bugs would look like weather bugs*, which on this project means
 * nobody would ever find them (**D2**). A continuous comfort value is a
 * temperature field wearing a different name. Four rungs can be named on screen,
 * so when it feels wrong haugur can say **which rung is wrong**.
 *
 * ## ⚠️ Every scan here is BOUNDED, and that is correctness, not thrift
 *
 * 🔴 **The world is 2,464 blocks tall** (`WORLD_MIN_Y` −64 … `WORLD_MAX_Y`
 * 2400). The obvious way to ask *"is there sky above you"* is to look straight
 * up until you run out of world — **up to 2,464 lookups across 77 chunks**, most
 * of which do not exist yet and each of which is a range request against a
 * 3.7 GB COG on another host. Per player, per tick. ⚠️ **It would fall over as a
 * network fault**, which is the least findable shape a bug can have here.
 *
 * ✅ **And the bounded answer is the RIGHT one.** A roof shelters you because it
 * is *near*. Rock 200 m above your head means you are at the foot of a cliff and
 * you are outdoors — a full-height scan would call that indoors. So the bound is
 * the design, not a budget.
 *
 * Everything in this file stays inside a box roughly {@link ESCAPE_RADIUS}
 * across, centred on the player — the chunks they are already standing in.
 *
 * ## Nothing here knows what the cold costs
 *
 * Deliberately. **Where you are** and **what that is worth** are two questions,
 * and D64's §9.3 warning is that guessing the second one is how this system
 * turns cruel. The numbers live with T131 and `tuning.json`.
 *
 * ## 🔴 THE TRAP WAITING FOR T131: {@link ShelteringAt} is SYNCHRONOUS and
 * `ServerWorld.blockAt` is NOT
 *
 * ⚠️ **Found while building this, and written down here so it is not discovered
 * halfway through the wiring.** `packages/server/src/world.ts` returns
 * `Promise<number>` — it may have to generate a chunk, which is a range request
 * against a COG on another host. **Awaiting inside the escape search would mean
 * thousands of awaits per player per tick**, and it would look like the server
 * hanging rather than like a shelter bug.
 *
 * ✅ **The fix is small and must happen at the CALL SITE, not in here.** Await
 * the chunks around the player once, then read synchronously out of their
 * `blocks` arrays. Everything in this file stays inside a box
 * `2 × ESCAPE_RADIUS + 1` = **17 m** across, and a chunk is 32 m, so that box
 * touches **at most 2 × 2 × 2 = 8 chunks** — and the player is standing in them,
 * so they are already in the cache.
 *
 * ⚠️ **The client has no such problem**: its chunks are already meshed and in
 * memory, which is the whole reason the same function can run on both ends.
 */

import { blockCutout, blockHeight, isFluid, isSolid, BLOCK_AIR } from './blocks.ts';
import { stateOverride } from './states.ts';
import type { WorldPosition } from './coords.ts';
import { EYE_HEIGHT } from './edits.ts';

/* ── the ladder ───────────────────────────────────────────────────────────── */

/**
 * The shelter ladder of **D64 §3**, worst first.
 *
 * ⚠️ **The order is load-bearing** — {@link SHELTER_RUNGS} is indexed to compare
 * two rungs, so inserting one in the middle changes what "better than" means.
 * Append, or renumber everything that reads it.
 *
 * | Rung | What D64 gives it |
 * |---|---|
 * | `open` | Full exposure — cold **and** wind |
 * | `lee` | **Wind removed.** Helps; does not solve it |
 * | `enclosed` | Most of the cold removed |
 * | `hearth` | **Exposure off** |
 */
export const SHELTER_RUNGS = ['open', 'lee', 'enclosed', 'hearth'] as const;

export type ShelterRung = (typeof SHELTER_RUNGS)[number];

/**
 * ✅ **Every rung is reachable — since 2026-08-31, and `hearth` was the last.**
 *
 * 🔴 **This said `enclosed` for ten days and the reason it gave went stale
 * without anybody noticing.** It read *"`hearth` needs a fire, and there is no
 * fire block"* — which was true when it was written and stopped being true on
 * 2026-08-27, when the hearth started burning. **The rung stayed unreachable
 * for four more days because the sentence explaining why was never re-read**,
 * and sitting by your own fire in a December storm cost exactly as much food as
 * standing in a field.
 *
 * ⚠️ **The prediction it made was right, though**: lighting the first fire was
 * a matter of answering {@link ShelterQuery.fireAt} and not of reopening the
 * ladder. 👉 **Keep this constant** — it is what the tests compare against, and
 * a future rung will arrive unreachable too.
 */
export const HIGHEST_REACHABLE_RUNG: ShelterRung = 'hearth';

/**
 * ☂️ **Is there something over your head?**
 *
 * 🔴 **ONE answer to that question, asked by everything that needs it.** The
 * cold asks it to decide how much of the chill a roof takes off; the rain asks it
 * to decide whether to fall on you at all. ⚠️ **They used to be two separate
 * expressions of the same idea**, and `HANDOFF.md` was explicit when rain was
 * being planned: *"`shelter.ts` already answers are you under a roof — do not
 * write a second answer to that question."*
 *
 * ⚠️ **The lee of a wall is NOT under a roof.** You are out of the wind and
 * still in the rain, which is exactly what standing behind a wall in Iceland is
 * like.
 */
export function underARoof(rung: ShelterRung): boolean {
  return rung === 'enclosed' || rung === 'hearth';
}

/* ── what counts as a roof ────────────────────────────────────────────────── */

/**
 * 🔴 **Does this block keep the weather off you?**
 *
 * ## ⚠️ This is the FOURTH question `solid` used to answer by accident
 *
 * `isSolid` means *"you cannot walk through it"*, `isTargetable` means *"the
 * crosshair can hit it"*, `isCutout` means *"you can see through it"* — and this
 * project has been caught **five times** by one rule quietly doing several jobs
 * (`blocks.ts`, T95, `Development/CLAUDE.md` rule 10). **Shelter is its own
 * question and so it gets its own name**, because a rule with a name is a rule
 * you can grep for.
 *
 * ## The rule: a full opaque cube, and nothing else
 *
 * 🎯 **haugur, 2026-08-21, asked whether standing under a birch shelters you:**
 * **no.** Leaves and scrub have holes in them, and the game already records how
 * much of each tile is hole.
 *
 * 🔴 **Why the answer matters more than it looks: 35 % of this world is birch
 * ground.** Had a crown counted as a roof, a third of the map would quietly have
 * been *indoors*, the cold would almost never have bitten, and the cause would
 * have been invisible — the same failure shape §6 bans the temperature field
 * for.
 *
 * | Block | Shelters? | Why |
 * |---|---|---|
 * | air | ❌ | nothing there |
 * | `vatn` and any fluid | ❌ | ⚠️ under water you are **drowning**, not sheltering (T134) |
 * | `kjarr` (scrub) | ❌ | a half-height tuft you walk through |
 * | `birkilauf` (leaves) | ❌ | haugur's call — the tile is part hole |
 * | an open door or gate | ❌ | a gap — not solid, so not a wall (D135) |
 * | `birkistofn`, móberg, turf, stone, anything built | ✅ | a full cube |
 */
export function sheltersFromWeather(id: number): boolean {
  if (id === BLOCK_AIR) return false;
  /* Under water you are drowning, not sheltering (T134). */
  if (isFluid(id)) return false;
  /* 🚪 A gap is not a wall. An open door or gate is not solid (`perState`), and
     counting it as shelter made an open doorway indoors (D135, the pit-house
     vefstofa). Measured 2026-10-05: the only blocks this changes are the four
     open gate states — and an open gate is not a wall either. */
  if (!isSolid(id)) return false;
  /* Part hole is not a roof — leaves, and haugur's call. */
  if (blockCutout(id) > 0) return false;
  /* Scrub covers nothing above it — it is half a metre and you walk through it.
     ⚠️ It was a quarter until 2026-09-09 and the answer did not change: the test
     is "does this reach the roof", and neither height comes close. */
  return blockHeight(id) >= 1;
}

/**
 * 🔥 **Does this block warm you?** — the fifth question `solid` used to answer
 * by accident, and it gets its own name for the reason all four others did.
 *
 * ## 🔴 It asks the STATE, not the block
 *
 * `eldstaedi` is one block key and **two** numbers. A cold hearth is still your
 * home and still holds your things (**D86**), and it must not warm anybody —
 * so this reads `block-states.json`'s `perState`, where lit and unlit can
 * differ, and **never `block-behaviour.json`**, where they cannot.
 *
 * ⚠️ **A hearth is 0.25 m tall and does not shelter you**, which is what lets
 * the room search in {@link isEnclosed} walk straight through its cell and find
 * it. The two rules are independent and both are right.
 */
export function warmsFromWeather(id: number): boolean {
  return stateOverride(id)?.warms === true;
}

/* ── how far each check looks ─────────────────────────────────────────────── */

/**
 * How far above your head a roof still counts as a roof, in metres.
 *
 * A turf house is about head height inside and a hraunhellir is not much more.
 * ⚠️ **Measured from the eye, not the feet** — see {@link EYE_HEIGHT} — so this
 * is roughly 6.7 m of clearance above where you stand.
 *
 * ⚠️ **A number to feel, not to argue.** If a roof at 5 m reads as open sky,
 * raise it; the cost of raising it is five more block lookups.
 */
export const ROOF_REACH = 5;

/**
 * How far you may wander before you count as having got outside, in metres.
 *
 * 🎯 **haugur's call, 2026-08-21: about 8 m.** A real turf house is small, and
 * if you cannot find your way out to open sky within eight metres of where you
 * stand, you are indoors.
 *
 * ⚠️ **It bounds the whole cost of this file.** The escape search never leaves a
 * box 17 m across, which is the chunk you are standing in and its neighbours —
 * already in memory, because you are standing in them.
 */
export const ESCAPE_RADIUS = 8;

/**
 * The hard cap on blocks the escape search may visit.
 *
 * 🔴 **A backstop, not the design.** {@link ESCAPE_RADIUS} is what decides the
 * answer; this stops a strangely-shaped cave costing more than the bound
 * implies. ⚠️ **Reaching it is treated as ENCLOSED** — see {@link isEnclosed}
 * for why that is the safe way round.
 */
export const ESCAPE_BUDGET = 900;

/**
 * How far upwind a wall may stand and still shelter you, in metres.
 *
 * Short on purpose: you have to actually be *behind* the wall. Standing four
 * metres out from the north side of a house in a northerly is being in its lee;
 * standing twenty metres out is being in a field.
 */
export const LEE_REACH = 4;

/* ── the query ────────────────────────────────────────────────────────────── */

/**
 * 🔴 **Every block this file can possibly read, as offsets from the head.**
 *
 * ⚠️ **This exists so the SERVER can pre-fetch exactly the right chunks**, and
 * so that claim stays true if the constants above ever move — see the async trap
 * in this file's header. Working it out by hand at the call site is how it goes
 * wrong silently.
 *
 * The escape search reaches {@link ESCAPE_RADIUS} in every direction, and from
 * each cell it reaches {@link ROOF_REACH} further **up** — so the box is taller
 * above than below. ⚠️ **The lee probe is shorter than the search, so it adds
 * nothing.**
 */
export const SHELTER_REACH = {
  horizontal: ESCAPE_RADIUS,
  down: ESCAPE_RADIUS,
  up: ESCAPE_RADIUS + ROOF_REACH,
} as const;

/** Is there something at this block that keeps the weather off you? */
export interface ShelteringAt {
  (x: number, y: number, z: number): boolean;
}

export interface ShelterQuery {
  /**
   * Where the player is standing, in world metres — **the feet**, as
   * `player_state` reports them. {@link EYE_HEIGHT} is added here.
   */
  readonly at: WorldPosition;
  /**
   * Degrees the wind blows **from**, or `null`.
   *
   * ⚠️ **`null` is the ordinary case, not an error.** The Veðurstofa archive
   * records no direction when the wind was calm or variable (`Weather.direction`),
   * and both mean *there is no lee to stand in* — calm wind has none, and
   * variable wind has none you could rely on. So `null` simply skips the check,
   * and it costs nothing because there is no wind to remove.
   */
  readonly windFrom: number | null;
  /** What the world contains — {@link sheltersFromWeather} over your blocks. */
  readonly shelters: ShelteringAt;
  /**
   * 🔥 **Is there a fire burning in this cell?** — {@link warmsFromWeather} over
   * your blocks, exactly as {@link shelters} is `sheltersFromWeather` over them.
   *
   * ## 🔴 It asks about a CELL, and that is the whole design
   *
   * ⚠️ **This used to be `fireNear: () => boolean`** — *is there a fire
   * somewhere near*, with the caller deciding what *near* meant. **A radius
   * would have warmed you through a wall**: stand on the far side of a turf
   * gable from your neighbour's hearth and the cold would switch off, which is
   * the shape of fault this file's header calls the least findable one.
   *
   * ✅ **So the geometry is decided HERE and the caller only reads blocks.** The
   * escape search already walks the air of the room you are standing in; a fire
   * counts when it stands in one of those cells. **That needs no new number** —
   * *the room* is haugur's own eight metres ({@link ESCAPE_RADIUS}), reused
   * rather than re-argued.
   *
   * ⚠️ **Omitted means no fire**, which is the honest answer for a caller that
   * cannot see block state.
   */
  readonly fireAt?: ShelteringAt;
}

/* ── the answer ───────────────────────────────────────────────────────────── */

/**
 * 🏠 Which rung of D64's shelter ladder you are on.
 *
 * ⚠️ **Pure arithmetic and a callback**, exactly like `raycastVoxels` beside it
 * and for the same reason: **the server owns the truth and the client draws it**,
 * and one function called by both is what stops the gauge on screen disagreeing
 * with the drain in the database.
 *
 * The ladder is walked from the top down, because the rungs are not independent
 * — a fire only helps you indoors, and being indoors already implies a roof.
 */
export function shelterAt(query: ShelterQuery): ShelterRung {
  const head = headBlock(query.at);

  /* 🔴 Standing inside a solid block. Being buried is not shelter, and it is
     what happens for one frame when the ground loads under a falling player —
     answering `enclosed` there would hand out free warmth on every spawn. */
  if (query.shelters(head.x, head.y, head.z)) return 'open';

  const room = roomAround(head, query.shelters);

  if (room.enclosed) {
    /* 🔥 A fire warms a ROOM. Out in the open it is a campfire in a gale, which
       D64 §3 does not give a rung to — so this is only ever asked once the walls
       and the roof have already answered.

       ⚠️ `some` over the cells the search just walked: the fire has to be in
       HERE, not merely nearby. See ShelterQuery.fireAt for what a radius would
       have got wrong. */
    const fire =
      query.fireAt !== undefined &&
      room.cells.some((cell) => query.fireAt?.(cell.x, cell.y, cell.z) === true);
    return fire ? 'hearth' : 'enclosed';
  }

  return inLee(head, query.windFrom, query.shelters) ? 'lee' : 'open';
}

/**
 * The block your head is in.
 *
 * ⚠️ **The head, not the feet.** A roof is over your head, and the wind hits you
 * there too — measuring from the feet would let a one-block kerb count as a wall.
 */
export function headBlock(at: WorldPosition): WorldPosition {
  return {
    x: Math.floor(at.x),
    y: Math.floor(at.y + EYE_HEIGHT),
    z: Math.floor(at.z),
  };
}

/**
 * Is there a roof close above this block?
 *
 * ⚠️ **{@link ROOF_REACH} lookups and no more** — see this file's header for why
 * scanning to the top of the world is both ruinous and wrong.
 */
export function isCovered(head: WorldPosition, shelters: ShelteringAt): boolean {
  for (let dy = 1; dy <= ROOF_REACH; dy++) {
    if (shelters(head.x, head.y + dy, head.z)) return true;
  }
  return false;
}

/**
 * Can the air around you find its way out to open sky within
 * {@link ESCAPE_RADIUS} metres?
 *
 * A breadth-first walk through the air, from your head outwards. It stops the
 * moment it finds a block that is **uncovered** — somewhere the sky reaches — and
 * that is the honest meaning of *"is there a way out"*. A roof alone is only an
 * overhang; a roof you cannot walk out from is a house.
 *
 * ## 🔴 Running out of budget answers ENCLOSED, and that is the safe way round
 *
 * ⚠️ Both wrong answers cost something, so the choice is which one is *findable*.
 * A cave wrongly called **open** makes the cold bite indoors — which reads as
 * *"the shelter is broken"*, a complaint aimed at the wrong system. A field
 * wrongly called **enclosed** makes the cold not bite — and the player is
 * standing in the open, looking at the sky, able to say exactly what is wrong.
 * ✅ **The second one is the one haugur can report** (**D2**), so it wins.
 *
 * ⚠️ In practice the budget is unreachable from any ordinary room: it is the
 * strangely-shaped-cave case, and it is logged nowhere because it is not an
 * error.
 */
export function isEnclosed(head: WorldPosition, shelters: ShelteringAt): boolean {
  return roomAround(head, shelters).enclosed;
}

/**
 * 🏠 **The room you are standing in** — whether it is sealed, and which cells it
 * is made of.
 *
 * ⚠️ **This is {@link isEnclosed}'s own search, returning what it walked over
 * instead of throwing it away.** It was split out on 2026-08-31 so that the
 * hearth rung could ask *is the fire in HERE* rather than *is a fire within N
 * metres*, which would have warmed you through a wall. **No second search and
 * no second bound** — one walk answers both questions, and keeping them in one
 * function is what stops them ever disagreeing about where the room ends.
 *
 * 🔴 **`cells` is only meaningful when `enclosed` is true.** A player under open
 * sky gets an empty list, because the search stops the moment it finds its way
 * out — and a "room" that reaches the sky is a field.
 */
export function roomAround(
  head: WorldPosition,
  shelters: ShelteringAt,
): { readonly enclosed: boolean; readonly cells: readonly WorldPosition[] } {
  /* No roof at all is the cheap way out, and it is the common case — most of
     the time a player is standing in a field. One lookup usually settles it. */
  if (!isCovered(head, shelters)) return { enclosed: false, cells: [] };

  /* ⚠️ Keys are strings rather than packed integers: world coordinates run to
     ±400,000 and a hand-rolled bit-packing would silently collide out there.
     The set never holds more than ESCAPE_BUDGET entries. */
  const seen = new Set<string>([key(head)]);
  const cells: WorldPosition[] = [head];
  const queue: WorldPosition[] = [head];
  let visited = 0;

  while (queue.length > 0) {
    const cell = queue.shift();
    if (cell === undefined) break;

    /* ⚠️ Reaching the budget answers ENCLOSED with the cells found SO FAR, which
       is the safe way round for both questions: a strangely-shaped cave still
       counts as indoors, and a fire in the part that was never walked simply
       does not warm you. Warmth degrades to `enclosed`; it never appears out of
       nowhere. */
    if (++visited > ESCAPE_BUDGET) return { enclosed: true, cells };

    /* Got out. Somewhere within reach, the sky comes down to this air. */
    if (!isCovered(cell, shelters)) return { enclosed: false, cells: [] };

    for (const step of NEIGHBOURS) {
      const next = { x: cell.x + step.x, y: cell.y + step.y, z: cell.z + step.z };

      /* Beyond the bound is beyond the question. haugur's eight metres: if the
         air has run this far and still has a roof on it, you are indoors. */
      if (chebyshev(next, head) > ESCAPE_RADIUS) continue;
      /* Air only — the search walks the room, not the walls. ⚠️ A hearth is a
         quarter-height plate and does NOT shelter, so the walk goes straight
         through its cell and `cells` contains it. That is what makes the fire
         findable without a second search. */
      if (shelters(next.x, next.y, next.z)) continue;

      const id = key(next);
      if (seen.has(id)) continue;
      seen.add(id);
      cells.push(next);
      queue.push(next);
    }
  }

  /* The air ran out without reaching the sky: a sealed room. */
  return { enclosed: true, cells };
}

/**
 * Is something upwind blocking the wind?
 *
 * 🎯 **This is the most Icelandic thing in the feature, and it was free.** The
 * weather already carries which direction the wind blows *from*, per player, per
 * zone (**D57**, **D58**, protocol 6) — so *get behind the wall* means the
 * **correct side** of the wall, and it changes when the wind changes.
 *
 * ⚠️ **A null direction is no lee**, and that is right rather than a gap — see
 * {@link ShelterQuery.windFrom}.
 */
export function inLee(
  head: WorldPosition,
  windFrom: number | null,
  shelters: ShelteringAt,
): boolean {
  if (windFrom === null) return false;

  /* Meteorological degrees: 0° is FROM the north, 90° FROM the east, and the
     angle turns clockwise. North is −z in this world (`world.ts` — z is the
     negated ISN93 northing), so walking INTO the wind is:
       north (0°)  → −z      east (90°) → +x
     which is exactly sin/−cos. ⚠️ Getting the sign wrong here puts the player in
     the lee of the wall on the windward side, and nothing would ever say so. */
  const radians = (windFrom * Math.PI) / 180;
  const stepX = Math.sin(radians);
  const stepZ = -Math.cos(radians);

  for (let d = 1; d <= LEE_REACH; d++) {
    const x = Math.floor(head.x + stepX * d);
    const z = Math.floor(head.z + stepZ * d);
    /* At head height, deliberately. A kerb you can see over does not stop wind,
       and D64 §3 gives the lee to a wall, a cave or an overhang. */
    if (shelters(x, head.y, z)) return true;
  }

  return false;
}

/* ── small helpers ────────────────────────────────────────────────────────── */

/** The six face neighbours. Diagonals are not a way out of a sealed room. */
const NEIGHBOURS: readonly WorldPosition[] = [
  { x: 1, y: 0, z: 0 },
  { x: -1, y: 0, z: 0 },
  { x: 0, y: 1, z: 0 },
  { x: 0, y: -1, z: 0 },
  { x: 0, y: 0, z: 1 },
  { x: 0, y: 0, z: -1 },
];

function key(at: WorldPosition): string {
  return `${at.x},${at.y},${at.z}`;
}

/**
 * Box distance — the largest of the three axis distances.
 *
 * ⚠️ **Not straight-line distance.** The bound is a box because the chunks it
 * must stay inside are boxes; a sphere would reach into the same chunks and be
 * harder to reason about.
 */
function chebyshev(a: WorldPosition, b: WorldPosition): number {
  return Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y), Math.abs(a.z - b.z));
}
